import { EventBuffer } from "../core/buffer"
import { uuid } from "../core/identity"
import { Transport, type TransportTargets } from "../core/transport"
import type { SessionEvent, SessionMeta } from "../core/types"
import type {
  NativeClient,
  NativePlatformInfo,
  ReliaNativeOptions,
  StorageLike,
} from "./types"
import {
  buildUserAgent,
  detectNativeDevice,
  getAsyncStorage,
  getPlatformInfo,
  getReactNative,
  getScreenSize,
  getWindowDims,
  resolveVisitorId,
} from "./platform"
import { captureNativeError, capturePress, captureScreenLeave, captureScreenView } from "./collectors"
import { patchGlobalNetwork } from "./network"
import { __setScreenTracker } from "./navigation"
import { startNativeReplay, type NativeReplayRecorder } from "./replay"

const FLUSH_INTERVAL_MS = 4000

export interface NativeDeps {
  platformInfo?: NativePlatformInfo
  screenSize?: string
  storage?: StorageLike | null
  appState?: {
    addEventListener: (ev: string, cb: (s: string) => void) => { remove: () => void } | void
  } | null
  /** Test seam: observe every event pushed into the buffer. */
  onEvent?: (event: SessionEvent) => void
}

function normalizePathPattern(pathname: string, patterns?: string[]): string {
  if (!patterns || patterns.length === 0) return pathname
  for (const pattern of patterns) {
    const pSeg = pattern.split("/").filter(Boolean)
    const aSeg = pathname.split("/").filter(Boolean)
    if (pSeg.length !== aSeg.length) continue
    let matched = true
    for (let i = 0; i < pSeg.length; i++) {
      if (pSeg[i].startsWith(":")) continue
      if (pSeg[i] !== aSeg[i]) {
        matched = false
        break
      }
    }
    if (matched) return pattern
  }
  return pathname
}

export let globalNativeClient: NativeClient | null = null

export async function initNative(options: ReliaNativeOptions, deps?: NativeDeps): Promise<NativeClient> {
  const endpoint = options.endpoint.replace(/\/+$/, "")
  const captureText = options.captureText ?? true
  const rn = getReactNative()
  const platformInfo = (deps && deps.platformInfo) || getPlatformInfo(rn)
  const screenSize =
    (deps && typeof deps.screenSize === "string" ? deps.screenSize : null) ??
    getScreenSize(getWindowDims(rn))
  const storage = deps && "storage" in deps ? deps.storage : getAsyncStorage()
  const visitorId = await resolveVisitorId(storage ?? null)
  const sessionId = uuid()
  const startedAt = new Date()
  const appName = options.app_name || ""
  const meta: SessionMeta = {
    session_id: sessionId,
    visitor_id: visitorId,
    project_id: options.project_id,
    project_name: options.project_name,
    service_name: appName,
    started_at: startedAt.toISOString(),
    user_agent: buildUserAgent(platformInfo, appName, options.app_version || ""),
    device: detectNativeDevice(platformInfo),
    screen_size: screenSize,
    user_id: options.user_id || "",
    user_email: options.user_email || "",
    user_traits: options.user_traits || {},
  }

  const targets: TransportTargets = {
    ingestUrl: `${endpoint}/sessions/ingest`,
    finalizeUrl: `${endpoint}/sessions/finalize`,
    replayUrl: `${endpoint}/sessions/replay`,
  }
  const transport = new Transport(targets, meta)
  const buffer = new EventBuffer(500)
  let eventCount = 0
  let errorCount = 0
  let stopped = false
  let uploading = false
  let currentScreen = ""
  let screenEnteredAt = Date.now()
  let replayChunkIndex = 0
  let hasReplayCaptured = false

  let recorder: NativeReplayRecorder | null = null
  if (options.replay !== false) {
    recorder = startNativeReplay(
      async (events) => {
        if (stopped) return
        hasReplayCaptured = true
        try {
          await transport.sendReplay(events, replayChunkIndex++)
        } catch {
          // dropped on failure
        }
      },
      () => currentScreen,
    )
  }

  function push(event: SessionEvent, isError = false): void {
    if (stopped) return
    if (deps && deps.onEvent) {
      try {
        deps.onEvent(event)
      } catch {
        // observer must never break capture
      }
    }
    buffer.push(event)
    eventCount += 1
    if (isError) errorCount += 1
  }

  async function flush(): Promise<void> {
    if (stopped || uploading) return
    const events = buffer.take()
    if (!events.length) return
    uploading = true
    try {
      await transport.sendJourney(events)
    } catch {
      for (const e of events) buffer.push(e)
    } finally {
      uploading = false
    }
  }

  function trackScreen(screenName: string, params?: Record<string, any>): void {
    const screen = normalizePathPattern(screenName || "unknown", options.routePatterns)
    if (currentScreen) {
      push(captureScreenLeave(currentScreen, Date.now() - screenEnteredAt))
    }
    currentScreen = screen
    screenEnteredAt = Date.now()
    const ev = captureScreenView(screen, params)
    ev.path = screen
    push(ev)
    void flush()
    recorder?.capture(screen)
  }

  __setScreenTracker((s) => trackScreen(s))

  function trackPress(target: string, properties?: Record<string, any>): void {
    const props = { screen: currentScreen, ...((properties || {}) as Record<string, any>) }
    const ev = capturePress(target, props)
    ev.text = captureText ? String(target || "").slice(0, 150) : ""
    push(ev)
    void flush()
    recorder?.capture(currentScreen)
  }

  function identify(userId: string, traits: Record<string, any> = {}): void {
    meta.user_id = userId
    if (traits.email && typeof traits.email === "string") meta.user_email = traits.email
    meta.user_traits = { ...(meta.user_traits || {}), ...traits }
    transport.updateMeta(meta)
    push({
      type: "custom",
      ts: new Date().toISOString(),
      target: "identify",
      path: currentScreen,
      text: `Identified: ${userId}`,
      meta: { user_id: userId, ...(traits as Record<string, string | number | boolean>) },
    })
    void flush()
  }

  function track(eventName: string, properties: Record<string, any> = {}): void {
    push({
      type: "custom",
      ts: new Date().toISOString(),
      target: eventName,
      path: currentScreen,
      text: eventName,
      meta: { ...(properties as Record<string, string | number | boolean>) },
    })
    void flush()
  }

  const restores: Array<() => void> = []
  const g = globalThis as any

  if (!options.disableErrorCapture && g.ErrorUtils && typeof g.ErrorUtils.setGlobalHandler === "function") {
    const prev = g.ErrorUtils.getGlobalHandler ? g.ErrorUtils.getGlobalHandler() : null
    const handler = (error: any, isFatal?: boolean) => {
      push(
        captureNativeError(error?.message || String(error), error?.stack || ""),
        true,
      )
      void flush()
      if (typeof prev === "function") {
        try {
          prev(error, isFatal)
        } catch {
          // previous handler must never break ours
        }
      }
    }
    g.ErrorUtils.setGlobalHandler(handler)
    restores.push(() => {
      try {
        if (typeof prev === "function") g.ErrorUtils.setGlobalHandler(prev)
      } catch {
        // restore is best-effort
      }
    })
  }

  const originalConsoleError = g.console && g.console.error ? g.console.error.bind(g.console) : null
  if (g.console && typeof g.console.error === "function") {
    g.console.error = (...args: unknown[]) => {
      if (originalConsoleError) originalConsoleError(...args)
      push(captureNativeError(args.map(String).join(" ").slice(0, 500), ""), false)
    }
    restores.push(() => {
      if (originalConsoleError) g.console.error = originalConsoleError
    })
  }

  if (!options.disableNetworkCapture) {
    restores.push(patchGlobalNetwork(push, endpoint))
  }

  const appState =
    (deps && "appState" in deps ? deps.appState : null) ??
    (rn && rn.AppState && typeof rn.AppState.addEventListener === "function" ? rn.AppState : null)
  let appStateSub: { remove: () => void } | void | null = null
  if (appState) {
    try {
      appStateSub =
        appState.addEventListener("change", (s: string) => {
          if (s === "background") void flush()
        }) || null
    } catch {
      appStateSub = null
    }
  }

  push({ type: "session_start", ts: meta.started_at, target: "", path: "", text: "", meta: {} })
  void flush()

  const timer = setInterval(() => {
    void flush()
  }, FLUSH_INTERVAL_MS)
  if (typeof (timer as any).unref === "function") (timer as any).unref()

  function stop(): void {
    if (stopped) return
    stopped = true
    clearInterval(timer)
    __setScreenTracker(null)
    for (const r of restores) {
      try {
        r()
      } catch {
        // restore is best-effort
      }
    }
    if (appStateSub && typeof appStateSub.remove === "function") {
      try {
        appStateSub.remove()
      } catch {
        // ignore
      }
    }
    recorder?.stop()
    void flush()
    transport.beaconFinalize({
      session_id: sessionId,
      visitor_id: visitorId,
      project_id: options.project_id,
      started_at: meta.started_at,
      ended_at: new Date().toISOString(),
      duration_ms: Date.now() - startedAt.getTime(),
      event_count: eventCount,
      error_count: errorCount,
      has_replay: hasReplayCaptured || Boolean(recorder),
      user_agent: meta.user_agent,
      device: meta.device,
      screen_size: meta.screen_size,
    })
  }

  const client: NativeClient = { stop, identify, track, trackScreen, trackPress }
  globalNativeClient = client
  return client
}

export function identify(userId: string, traits?: Record<string, any>): void {
  globalNativeClient?.identify(userId, traits)
}

export function track(eventName: string, properties?: Record<string, any>): void {
  globalNativeClient?.track(eventName, properties)
}

export function trackScreen(screenName: string, params?: Record<string, any>): void {
  globalNativeClient?.trackScreen(screenName, params)
}

export function trackPress(target: string, properties?: Record<string, any>): void {
  globalNativeClient?.trackPress(target, properties)
}

export function stop(): void {
  globalNativeClient?.stop()
}
